import { createElement, useRef, useLayoutEffect, useCallback } from "react"

/**
 * Animator takes children and adds scroll based animation
 */
export default function Animator({
  children,
  classes,
  element,
  translateX,
  opacity,
}) {
  const activeItem = useRef()

  const type = element ? element : "div"

  const animatedElm = createElement(
    type,
    { className: classes, ref: activeItem },
    children
  )

  const updateTransform = useCallback(
    progress => {
      if (translateX) {
        const finishX = parseInt(translateX * progress, 10)
        activeItem.current.style.transform = `translateX(${finishX}px)`
      }
    },
    [translateX]
  )

  const updateOpacity = useCallback(
    progress => {
      if (opacity !== undefined) {
        const startOpacity = 1
        const finishOpacity = startOpacity + (opacity - startOpacity) * progress
        activeItem.current.style.opacity = finishOpacity
      }
    },
    [opacity]
  )

  useLayoutEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false

    // Set some default easing to make the animation smoother
    activeItem.current.style.WebkitTransition = "all 1s ease-out"
    activeItem.current.style.MozTransition = "all 1s ease-out"

    const onScroll = () => {
      const rangeStart = 0
      const rangeFinish = window.innerHeight
      const rangeValue = rangeFinish - rangeStart
      const elDiff =
        window.innerHeight - activeItem.current.getBoundingClientRect().top
      const progress = Math.max(Math.min(elDiff / rangeValue, 1), 0)

      if (!prefersReducedMotion) {
        // Update properties
        updateTransform(progress)
        updateOpacity(progress)
      }
    }

    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [updateTransform, updateOpacity])

  return animatedElm
}
